import { isStackEmpty } from './stack-utils.js';

/**
 * Shared helpers for container UIs (chest, furnace, etc.).
 * Eliminates duplication of tooltip / item-description logic.
 */
export function createContainerTooltip({ itemDefs, t, tooltipEl }) {

  function getItemDisplayName(itemId) {
    if (!itemId) return '';
    const key = `item.${itemId}`;
    const localized = t(key);
    return localized === key ? (itemDefs[itemId]?.name || itemId) : localized;
  }

  function describeItem(def, stack) {
    if (!def) return '';
    if (def.desc) return def.desc;

    if (def.kind === 'tool') {
      const toolName = def.toolType === 'pickaxe'
        ? t('ui.tool.pickaxe')
        : def.toolType === 'axe'
          ? t('ui.tool.axe')
          : def.toolType === 'shovel'
            ? t('ui.tool.shovel')
            : def.toolType === 'hoe'
              ? t('ui.tool.hoe')
              : def.toolType === 'sword'
                ? t('ui.tool.sword')
                : def.toolType === 'bow'
                  ? t('ui.tool.bow')
                  : def.toolType === 'shield'
                    ? t('ui.tool.shield')
                    : def.toolType === 'flint_steel'
                      ? t('ui.tool.flint_steel')
                      : def.toolType === 'shears'
                        ? t('ui.tool.shears')
                        : def.toolType === 'bucket'
                          ? t('ui.tool.bucket')
                          : t('ui.tool.shovel');
      const atk = def.attackDamage
        ? t('ui.tool.atk', { value: Number(def.attackDamage).toFixed(1) })
        : '';
      const dura = stack && Number(def.maxDurability) > 0
        ? t('ui.tool.durability', {
          value: Math.max(0, Math.floor(Number(stack.durability ?? def.maxDurability))),
          max: Math.floor(Number(def.maxDurability))
        })
        : '';
      return `${toolName}${atk}${dura}`.trim();
    }

    if (def.kind === 'armor') {
      const slotName = t(`ui.armor.slot.${def.armorSlot}`);
      const dura = stack && Number(def.maxDurability) > 0
        ? t('ui.tool.durability', {
          value: Math.max(0, Math.floor(Number(stack.durability ?? def.maxDurability))),
          max: Math.floor(Number(def.maxDurability))
        }).trim()
        : '';
      return t('ui.armor.desc', {
        value: Number(def.armor || 0),
        slot: slotName,
        durability: dura ? ` ${dura}` : ''
      }).trim();
    }

    if (def.kind === 'food') {
      const nutrition = Number(def.nutrition || 0);
      const heal = Number(def.heal || 0);
      if (def.isMedicine) {
        return t('ui.medicine.desc', { heal });
      }
      return t('ui.food.desc', {
        nutrition,
        heal: heal > 0 ? t('ui.food.heal', { value: heal }) : ''
      });
    }

    if (def.kind === 'block') return t('ui.block.desc');
    if (def.kind === 'material') return t('ui.material.desc');
    return t('ui.item.desc');
  }

  function showTooltip(stack, x, y) {
    if (!tooltipEl || isStackEmpty(stack)) {
      if (tooltipEl) tooltipEl.style.display = 'none';
      return;
    }

    const def = itemDefs[stack.id];
    if (!def) {
      tooltipEl.style.display = 'none';
      return;
    }

    const countText = stack.count > 1 ? ` x${stack.count}` : '';
    tooltipEl.innerHTML = `
      <div class="tip-name">${getItemDisplayName(stack.id)}${countText}</div>
      <div class="tip-desc">${describeItem(def, stack)}</div>
    `;
    tooltipEl.style.display = 'block';
    tooltipEl.style.left = `${x + 16}px`;
    tooltipEl.style.top = `${y + 16}px`;
  }

  function hideTooltip() {
    if (tooltipEl) tooltipEl.style.display = 'none';
  }

  function bindSlotTooltip(el, getStackFn) {
    if (!el || typeof getStackFn !== 'function') return;
    el.addEventListener('mouseenter', (e) => showTooltip(getStackFn(), e.clientX, e.clientY));
    el.addEventListener('mousemove', (e) => showTooltip(getStackFn(), e.clientX, e.clientY));
    el.addEventListener('mouseleave', hideTooltip);
  }

  return { getItemDisplayName, describeItem, showTooltip, hideTooltip, bindSlotTooltip };
}
